/**
 * Per-user date display format.
 *
 * A user picks one of the common formats on My Profile; it is stored with
 * their profile and applied wherever the app formats a date through
 * `formatDate` (lib/utils) or the schedule grid. No choice means no change:
 * each surface keeps the format it always had.
 *
 * This module has no imports on purpose. `lib/utils` depends on it, and utils
 * is imported by nearly everything, so it must not pull in the store or a
 * service.
 */

export type DateFormatId =
  | 'MMM D, YYYY'
  | 'MM/DD/YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY-MM-DD'
  | 'DD-MMM-YYYY'
  | 'D MMM YYYY'
  | 'DD.MM.YYYY'

const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad2 = (n: number) => String(n).padStart(2, '0')

/** Format a calendar date (month is 1-12). */
export function formatDateParts(year: number, month: number, day: number, format: DateFormatId): string {
  const mon = MONTHS_ABBR[month - 1] ?? '???'
  switch (format) {
    case 'MMM D, YYYY': return `${mon} ${day}, ${year}`
    case 'MM/DD/YYYY': return `${pad2(month)}/${pad2(day)}/${year}`
    case 'DD/MM/YYYY': return `${pad2(day)}/${pad2(month)}/${year}`
    case 'YYYY-MM-DD': return `${year}-${pad2(month)}-${pad2(day)}`
    case 'DD-MMM-YYYY': return `${pad2(day)}-${mon}-${year}`
    case 'D MMM YYYY': return `${day} ${mon} ${year}`
    case 'DD.MM.YYYY': return `${pad2(day)}.${pad2(month)}.${year}`
  }
}

/** The formats a user can choose, in the order the picker lists them. */
export const DATE_FORMATS: Array<{ id: DateFormatId; label: string; example: string }> = (
  [
    ['MMM D, YYYY', 'Month name first'],
    ['MM/DD/YYYY', 'US numeric'],
    ['DD/MM/YYYY', 'Day first, numeric'],
    ['DD.MM.YYYY', 'Day first, dotted'],
    ['DD-MMM-YYYY', 'Day, month name'],
    ['D MMM YYYY', 'Day, month name, spaced'],
    ['YYYY-MM-DD', 'ISO 8601'],
  ] as Array<[DateFormatId, string]>
).map(([id, label]) => ({ id, label, example: formatDateParts(2026, 3, 9, id) }))

const KNOWN = new Set<string>(DATE_FORMATS.map(f => f.id))

/** A stored value as a format id, or null when it is blank or unknown. */
export function toDateFormatId(value: unknown): DateFormatId | null {
  return typeof value === 'string' && KNOWN.has(value) ? (value as DateFormatId) : null
}

/** True when the format writes the day before the month in numbers, which is
 *  also how a typed 03/09 should be read. */
export function isDayFirst(format: DateFormatId | null): boolean {
  return format === 'DD/MM/YYYY' || format === 'DD.MM.YYYY'
}

let active: DateFormatId | null = null

/** The signed-in user's chosen format, or null when they have not chosen one. */
export function getActiveDateFormat(): DateFormatId | null {
  return active
}

export function setActiveDateFormat(format: DateFormatId | null): void {
  active = format
}

/**
 * Format an ISO date ('YYYY-MM-DD', with or without a time part) in the user's
 * format. Returns `fallback` when they have not chosen one or the value is not
 * a date, so a caller keeps whatever it showed before.
 */
export function formatIsoDatePreferred(iso: string | null | undefined, fallback: string): string {
  if (!active || !iso) return fallback
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return fallback
  return formatDateParts(Number(m[1]), Number(m[2]), Number(m[3]), active)
}

const MONTHS3 = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

/**
 * Parse a date the way a scheduler types it in Excel. Accepts ISO
 * (2026-11-18), numeric (11/18, 11/18/26, 11-18-2026, 18.11.2026), and
 * month-name forms (18-Nov, Nov 18, 18 nov 26). Missing years default to the
 * reference date's year. Returns an ISO string or null when it is not a date.
 *
 * Numeric dates are read month first unless the user's format is day first,
 * so 03/09 is 9 March for one user and 3 September for another, matching what
 * each of them sees on screen.
 */
export function parseTypedDate(raw: string, referenceIso: string, dayFirst: boolean = isDayFirst(active)): string | null {
  const s = raw.trim().toLowerCase()
  if (!s) return null
  const refYear = /^\d{4}/.test(referenceIso) ? parseInt(referenceIso.slice(0, 4), 10) : new Date().getFullYear()
  const mk = (y: number, m: number, d: number): string | null => {
    if (y < 100) y += 2000
    if (m < 1 || m > 12 || d < 1 || d > 31) return null
    const dt = new Date(Date.UTC(y, m - 1, d))
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  }
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) return mk(+m[1]!, +m[2]!, +m[3]!)
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/)
  if (m) {
    const year = m[3] ? +m[3] : refYear
    return dayFirst ? mk(year, +m[2]!, +m[1]!) : mk(year, +m[1]!, +m[2]!)
  }
  m = s.match(/^(\d{1,2})[ -]([a-z]{3,9})(?:[ -](\d{2}|\d{4}))?$/)
  if (m) {
    const mon = MONTHS3.indexOf(m[2]!.slice(0, 3)) + 1
    if (mon > 0) return mk(m[3] ? +m[3] : refYear, mon, +m[1]!)
  }
  m = s.match(/^([a-z]{3,9})[ -](\d{1,2})(?:[, -]+(\d{2}|\d{4}))?$/)
  if (m) {
    const mon = MONTHS3.indexOf(m[1]!.slice(0, 3)) + 1
    if (mon > 0) return mk(m[3] ? +m[3] : refYear, mon, +m[2]!)
  }
  return null
}
